import React, { useEffect, useState } from 'react';
import { useDispatch, useSelector } from 'react-redux';
import { useParams, useHistory } from 'react-router-dom';
import _ from 'underscore';
import { ReactComponent as NoRoomIcon } from '../../assets/icon/noRoom.svg';
import AddRoomModal from './AddRoomModal';
import Cards from '../../design-components/Cards';
import ModalDelete from '../../components/ModalDelete';
import { fetchRooms, fetchRoomById, deleteRoom } from '../../state/actions/room';
import { setActiveTab } from '../../state/actions/tab';
import './RoomManagement.css';

export default function RoomManagement() {
  const dispatch = useDispatch();
  const history = useHistory();
  const { siteId, siteName } = useParams();
  const [search, setSearch] = useState('');
  const [searchError, setSearchError] = useState();
  const [isModalVisible, setIsModalVisible] = useState(false);
  const [editingRoom, setEditingRoom] = useState(null);
  const [deleteModal, setDeleteModal] = useState({ isOpen: false, room: null });
  
  const { rooms, loading, currentRoom, activeTab } = useSelector((state) => ({
    rooms: state.room.rooms,
    loading: state.room.loading,
    currentRoom: state.room.currentRoom,
    activeTab: state.tab?.tab,
  }));

  // Set active tab when component mounts
  useEffect(() => {
    dispatch(setActiveTab({ tab: 'roomManagement' }));
  }, [dispatch]);

  // Fetch rooms with debounced search
  useEffect(() => {
    if (siteId && activeTab === 'roomManagement') {
      const timeoutId = setTimeout(() => {
        dispatch(fetchRooms({ siteId, search }));
      }, search ? 500 : 0); // No delay for initial load, 500ms delay for search
      
      return () => clearTimeout(timeoutId);
    }
  }, [dispatch, siteId, activeTab, search]);

  const handleSearch = (e) => {
    setSearchError(null);
    const value = e.target.value;
    // Remove regex validation for now since regexInput is not imported
    setSearch(value);
  };

  const onNewRoomHandler = () => {
    setEditingRoom(null);
    setIsModalVisible(true);
  };

  const handleModalCancel = () => {
    setIsModalVisible(false);
    setEditingRoom(null);
  };

  const handleModalSubmit = async (values) => {
    if (siteId) {
      dispatch(fetchRooms({ siteId, search }));
    }
  };

  const handleEditRoom = async (room) => {
    try {
      // Fetch the full room details
      await dispatch(fetchRoomById(room._id));
      setEditingRoom(room);
      setIsModalVisible(true);
    } catch (error) {
      console.error('Error fetching room details:', error);
    }
  };

  const handleDeleteRoom = (room) => {
    setDeleteModal({ isOpen: true, room });
  };

  const onDeleteItemHandler = async () => {
    try {
      await dispatch(deleteRoom(deleteModal.room._id));
      // Refresh the room list
      if (siteId) {
        dispatch(fetchRooms({ siteId, search }));
      }
      setDeleteModal({ isOpen: false, room: null });
    } catch (error) {
      console.error('Error deleting room:', error);
    }
  };

  const onCloseModalHandler = () => {
    setDeleteModal({ isOpen: false, room: null });
  };

  const handleRoomClick = (room) => {
    history.push(`/${siteId}/information/${siteName}/room/${room._id}`);
  };

  return (
    <div>
      <div className="screens__nav">
        <div className="screens__search-wrapper">
          <div className="media-categories__search">
            <i className="feather-search" />
            <input
              type="text"
              placeholder="Search..."
              value={search}
              onChange={handleSearch}
            />
          </div>
          {searchError && <div className="search-error">{searchError}</div>}
        </div>

        <div className="screens__button-group">
          <button className="button-primary" onClick={onNewRoomHandler}>
            <i className="feather-plus" /> Add Room
          </button>
        </div>
      </div>

      {loading ? (
        <div className="loading-container" style={{
          display: 'flex',
          justifyContent: 'center',
          alignItems: 'center',
          height: '50vh'
        }}>
          <p>Loading rooms...</p>
        </div>
      ) : rooms && rooms.length > 0 ? (
        <div className="media-list-content">
          {_.chunk(rooms, 3).map((chunk, index) => (
            <div className="media-list-chunk" key={index}>
              {chunk.map((room) => (
                <div key={room._id} className="media-list">
                  <Cards
                    data={room}
                    type="room"
                    isShowMedia={true}
                    editAction={() => handleEditRoom(room)}
                    deleteAction={() => handleDeleteRoom(room)}
                    cardClickAction={() => handleRoomClick(room)}
                    isEdit={true}
                    isDelete={true}
                    isPreview={false}
                    isDownload={false}
                    isClickable={true}
                  />
                </div>
              ))}
              {chunk.length < 2 && <div className="media-list-empty" />}
              {chunk.length < 3 && <div className="media-list-empty" />}
            </div>
          ))}
        </div>
      ) : (
        <div className="no-room-found" style={{
          display: 'flex',
          flexDirection: 'column',
          alignItems: 'center',
          justifyContent: 'center',
          height: '50vh',
          textAlign: 'center',
          color:'#9E9E9E'
        }}>
          <NoRoomIcon />
          <p>No rooms registered</p>
        </div>
      )}

      <AddRoomModal
        isVisible={isModalVisible}
        onCancel={handleModalCancel}
        onSubmit={handleModalSubmit}
        editingRoom={editingRoom}
      />

      <ModalDelete
        isVisible={deleteModal.isOpen}
        title="Delete Room"
        // subtitle={`Room: ${deleteModal.room?.name}`}
        onOk={onDeleteItemHandler}
        onCancel={onCloseModalHandler}
        loading={loading}
      />
    </div>
  );
}
